import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { keyframes } from 'styled-components';

import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconCheckCircle } from '#src/atoms/icons/system/iconCheckCircle.ts';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyM, HeadlineL } from '#src/atoms/typography/index.tsx';
import { UserAvatarWithFlag } from '#src/components/jonum/JonumVerificationDialog/AccountVerification/shared/UserAvatarWithFlag.tsx';
import { LegalWarningBanner } from '#src/components/jonum/LegalWarningBanner/index.tsx';

const COUNTDOWN_SECONDS = 5;

const Root = styled(Vertical).attrs({ gap: 0 })`
  position: relative;
  width: 100%;
  height: 100%;
  background: var(--c-black);
  overflow: hidden;
`;

const Content = styled(Vertical)`
  position: relative;
  z-index: 1;
  flex: 1;
  align-items: center;
  justify-content: center;
  padding: var(--quadruple-unit) var(--double-unit);
  gap: var(--quadruple-unit);
`;

const IconRow = styled(Horizontal).attrs({ gap: 1, center: true })`
  width: 100%;
`;

const Dots = styled.div`
  display: flex;
  gap: var(--half-unit);
  &::before,
  &::after {
    content: '';
  }
  & > *,
  &::before,
  &::after {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: var(--c-nd-400);
  }
`;

const SorareIcon = styled(Horizontal).attrs({ gap: 0, center: true })`
  position: relative;
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: var(--c-blue-400);
  flex-shrink: 0;
`;

const CheckBadge = styled(FontAwesomeIcon)`
  position: absolute;
  bottom: -4px;
  right: -4px;
  font-size: 24px;
  color: var(--c-green-500);
  background: var(--c-black);
  border-radius: 50%;
`;

const TextContent = styled(Vertical)`
  width: 100%;
  align-items: center;
  text-align: center;
  gap: var(--unit);
`;

const Title = styled(HeadlineL)`
  color: var(--c-white);
  width: 100%;
`;

const Description = styled(BodyM)`
  color: var(--c-nd-600);
  width: 100%;
`;

const Actions = styled(Vertical)`
  position: relative;
  z-index: 1;
  padding: 0 var(--double-unit) var(--double-unit);
  width: 100%;
`;

const fillProgress = keyframes`
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
`;

const ProgressButton = styled(Button)`
  position: relative;
  overflow: hidden;
  isolation: isolate;
`;

const ProgressFill = styled.div`
  position: absolute;
  inset: 0;
  background: var(--c-blue-400);
  transform-origin: left;
  animation: ${fillProgress} ${COUNTDOWN_SECONDS}s linear forwards;
  z-index: -1;
`;

const SorareGlyph = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 105 105"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M48.3264 29.8298C51.1347 33.3319 54.7542 36.0969 58.871 37.8875C62.9878 39.678 67.477 40.4432 71.9534 40.1142C72.1345 40.0938 72.3156 40.127 72.4789 40.206C72.6421 40.2851 72.7798 40.4101 72.8742 40.5657C72.9686 40.7213 73.0171 40.8998 73.012 41.0809C73.0094 41.262 72.9533 41.438 72.8538 41.591C70.3924 45.3456 68.8824 49.6436 68.4513 54.1124C68.0203 58.5811 68.6809 63.0882 70.3796 67.2433C70.446 67.4091 70.4664 67.5876 70.4358 67.7636C70.4052 67.9396 70.3286 68.1028 70.2113 68.2355C70.094 68.3681 69.9409 68.465 69.77 68.5161C69.5991 68.5671 69.418 68.5696 69.2471 68.5237C64.911 67.3376 60.3504 67.228 55.9606 68.1998C51.5709 69.1716 47.4847 71.1968 44.0541 74.102C43.9163 74.2143 43.7505 74.2883 43.5745 74.3138C43.3985 74.3393 43.22 74.3138 43.0567 74.2423C42.8935 74.1709 42.7532 74.0587 42.6512 73.9107C42.5492 73.7628 42.4905 73.5945 42.4777 73.4159C42.2609 68.9344 40.9575 64.5702 38.6798 60.7033C36.402 56.8365 33.2188 53.5818 29.4055 51.2199C29.255 51.1255 29.1351 50.9903 29.056 50.8296C28.977 50.6689 28.9464 50.4929 28.9617 50.3144C28.9795 50.1384 29.0433 49.9675 29.1504 49.8272C29.2575 49.6844 29.4029 49.5747 29.5662 49.5109C33.7672 47.9218 37.5192 45.3354 40.4984 41.9736C43.4776 38.6118 45.5921 34.5741 46.6609 30.2124C46.7017 30.039 46.7909 29.8834 46.9185 29.7584C47.046 29.6334 47.2042 29.5467 47.375 29.5085C47.5485 29.4702 47.7296 29.4778 47.8954 29.5365C48.0637 29.5952 48.2117 29.6972 48.3239 29.8349Z"
      fill="currentColor"
    />
  </svg>
);

type Props = {
  onConfirm: () => void;
};

export const JonumVerifiedView = ({ onConfirm }: Props) => {
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);

  useEffect(() => {
    if (secondsLeft <= 0) return undefined;
    const timeoutId = window.setTimeout(() => {
      setSecondsLeft(s => s - 1);
    }, 1000);
    return () => window.clearTimeout(timeoutId);
  }, [secondsLeft]);

  const ready = secondsLeft <= 0;

  return (
    <Root>
      <Content>
        <IconRow>
          <UserAvatarWithFlag />
          <Dots>
            <span />
            <span />
            <span />
          </Dots>
          <SorareIcon>
            <SorareGlyph />
            <CheckBadge icon={iconCheckCircle} />
          </SorareIcon>
        </IconRow>
        <TextContent>
          <Title bold>
            <FormattedMessage defaultMessage="Votre compte est vérifié" />
          </Title>
          <Description>
            <FormattedMessage defaultMessage="Merci ! Votre compte a été vérifié avec succès. Vous pouvez maintenant recevoir toutes vos récompenses." />
          </Description>
        </TextContent>
      </Content>
      <Actions>
        <ProgressButton
          size="medium"
          color="primary"
          fullWidth
          disabled={!ready}
          onClick={onConfirm}
        >
          {!ready && <ProgressFill />}
          {ready ? (
            <FormattedMessage defaultMessage="Votre compte est vérifié" />
          ) : (
            <FormattedMessage
              defaultMessage="Votre compte est vérifié ({seconds})"
              values={{ seconds: secondsLeft }}
            />
          )}
        </ProgressButton>
      </Actions>
      <LegalWarningBanner />
    </Root>
  );
};
